import type {
  CapabilityState,
  CloudflareAccessApplication,
} from "../shared/types";
import { CapabilityBadge, EmptyState } from "./AdminUi";
import { PaginationControls, usePagination } from "./pagination";

export function CloudflareApplicationsSection({
  applications,
  capability,
  onManage,
  onRevoke,
}: {
  applications: CloudflareAccessApplication[];
  capability: CapabilityState;
  onManage: (application: CloudflareAccessApplication) => void;
  onRevoke: (application: CloudflareAccessApplication) => void;
}) {
  const [pageSlice, setPage] = usePagination(applications, 10);
  return (
    <section className="directory-card cloudflare-section">
      <header className="collection-header">
        <div>
          <h2>Access applications</h2>
          <p>Authentication routing, policy coverage, and session controls.</p>
        </div>
        <div className="collection-header__actions">
          <CapabilityBadge capability={capability} />
          <span className="collection-header__count">
            {applications.length}
          </span>
        </div>
      </header>
      <div className="table-wrap">
        <table className="cloudflare-apps-table">
          <caption className="sr-only">Cloudflare Access applications</caption>
          <thead>
            <tr>
              <th>Application</th>
              <th>Type</th>
              <th>Session</th>
              <th>Identity routing</th>
              <th>Policies</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageSlice.items.map((application) => (
              <tr key={application.id}>
                <td data-label="Application">
                  <strong>{application.name}</strong>
                  <small>{application.domain}</small>
                </td>
                <td data-label="Type">{application.type}</td>
                <td data-label="Session">
                  {application.sessionDuration ?? "Default"}
                </td>
                <td data-label="Identity routing">
                  <strong>
                    {application.instantAuth ? "Instant Auth" : "Login picker"}
                  </strong>
                  <small>
                    {application.allowedIdentityProviders.length
                      ? `${application.allowedIdentityProviders.length} IdP allowed`
                      : "Account defaults"}
                  </small>
                </td>
                <td data-label="Policies">
                  {application.policies.length ? (
                    <div className="policy-stack">
                      {application.policies.map((policy) => (
                        <span key={policy.id}>
                          <strong>{policy.name}</strong>
                          <small>
                            {policy.decision}
                            {policy.temporaryAuthentication
                              ? " · temporary approval"
                              : policy.purposeJustificationRequired
                                ? " · justification"
                                : ""}
                          </small>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="event-outcome event-outcome--failed">
                      No policies
                    </span>
                  )}
                </td>
                <td data-label="Actions">
                  <div className="table-actions table-actions--wrap">
                    <button
                      className="button button--primary button--compact"
                      onClick={() => onManage(application)}
                      type="button"
                    >
                      Configure
                    </button>
                    <button
                      className="button button--secondary button--compact"
                      onClick={() => onRevoke(application)}
                      type="button"
                    >
                      Revoke sessions
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!applications.length && (
          <EmptyState
            hint="Configure the integration or check API permissions."
            title="No Access applications loaded"
          />
        )}
      </div>
      <PaginationControls
        label="Access applications"
        onPageChange={setPage}
        slice={pageSlice}
      />
    </section>
  );
}
